import {
  CarryOutTwoTone,
  CloseOutlined,
  CreditCardTwoTone,
  FallOutlined,
  PushpinTwoTone,
} from '@ant-design/icons';
import { Col, Descriptions, Form, Row, Typography } from 'antd';
import { message } from '@/components/Message';
import { FormInstance } from 'antd/es/form';
import React, { ComponentProps } from 'react';
import CardUserView from '@/components/Card/CardUser';
import CouponCom from '@/components/Coupon';
import Price from '@/components/Price';
import { CardType, CardUser, WriteOffCardUserList } from '@/models/card';
import { Coupon } from '@/models/coupon';
import { PreOrder } from '@/models/order';
import { PointRules } from '@/models/point';
import { calcSpecificationsTotalNumber } from '@/models/pos';
import { playSound } from '@/utils/sound';
import PointConsume from '../PointConsume';
import styles from '../style.less';
import WaiverAmountSelector from '../WaiverAmountSelector';

type Props = {
  form: FormInstance;
  preOrder: PreOrder;
  pointRules: PointRules;
  balancePayAvailable?: boolean;
  selectedCoupon: Coupon;
  onSelectedCouponChange: (selectedCoupon: Coupon) => void;
  selectedCards: { [cardUserId: number]: number };
  onSelectedCardsChange: (card: CardUser, number: number) => void;
};

const CartTradeArea: React.FC<Props> = ({
  // form,
  preOrder,
  pointRules,
  selectedCoupon,
  onSelectedCouponChange,
  selectedCards,
  onSelectedCardsChange,
}: Props) => {
  return (
    <>
      {preOrder.userFullInfo && preOrder.userFullInfo.cardList.length > 0 && (
        <>
          <div className={styles.item}>
            <h3>
              <CreditCardTwoTone /> 客户持有套餐卡
            </h3>
            <div>
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                  prev.writeOffCardUserList !== cur.writeOffCardUserList
                }>
                {({ getFieldValue }) => (
                  <Row gutter={[18, 10]}>
                    {preOrder.userFullInfo.cardList.map(card => {
                      const writeOffCardUserList = (getFieldValue('writeOffCardUserList') ||
                        []) as WriteOffCardUserList;

                      const selectedCardAndSkuInfoList = writeOffCardUserList.filter(
                        item => item.cardUserId === card.id
                      );
                      // 商品计算过滤后，实际选中的数量
                      const selectedCount = selectedCardAndSkuInfoList.reduce(
                        (acc, cur) => acc + cur.number,
                        0
                      );

                      // 可用于套餐卡结算的商品
                      const matchesSku = (preOrder.specifications || []).filter(sku =>
                        (card.cardSpecificationId || []).includes(sku.id)
                      );

                      const matchesSkuNum = matchesSku.reduce((num, sku) => num + sku.number, 0);

                      // 可优化：
                      // 如果【可用于套餐卡结算的商品】的总数 === 已进入待核销列表的相关商品的总数
                      // 那么， max 为 selectedCount

                      const selectableInfo: ComponentProps<
                        typeof CardUserView
                      >['selectableInfo'] = {
                        // 最大可选: Math.min(商品数量, 卡可用次数(次卡) | 无限(时长卡))
                        max: Math.min(
                          matchesSkuNum,
                          card.cardType === CardType.TIMES ? card.times : Number.MAX_SAFE_INTEGER
                        ),
                        available: matchesSku.length > 0,
                      };

                      if (matchesSku.length === 0) {
                        selectableInfo.NAReason = '无可用服务与商品';
                      }

                      return (
                        <Col span={24} key={card.id}>
                          <CardUserView
                            selectMode
                            card={card}
                            selectableInfo={selectableInfo}
                            // selectCount={selectedCards?.[card.id] || 0}
                            selectCount={selectedCount}
                            onSelect={number => {
                              onSelectedCardsChange(
                                card,
                                number
                                // Object.assign({}, selectedCards, {
                                //   [card.id]: number,
                                // })
                              );
                            }}
                          />
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Form.Item>
            </div>
          </div>
        </>
      )}

      {preOrder.userFullInfo && preOrder.userFullInfo.couponUserList.length > 0 && (
        <>
          <div className={styles.item}>
            <h3>
              <PushpinTwoTone /> 客户持有优惠券
            </h3>
            <div>
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                  prev.goodsPrice !== cur.goodsPrice || prev.cardPrice !== cur.cardPrice
                }>
                {({ getFieldValue }) => (
                  <Row gutter={[18, 10]}>
                    {preOrder.userFullInfo.couponUserList.map(coupon => {
                      return (
                        <Col span={8} key={coupon.id}>
                          <CouponCom
                            // 优惠券结算价： 商品价-套餐卡抵扣
                            price={getFieldValue('goodsPrice').minus(
                              getFieldValue('cardPrice') || 0
                            )}
                            coupon={coupon}
                            selected={selectedCoupon && selectedCoupon.id === coupon.id}
                            onInvalidSelected={reason => {
                              message.error(reason);
                            }}
                            onSelectedChange={(selected, coupon) => {
                              playSound('click');
                              if (selected) {
                                // setSelectedCoupon(coupon);
                                onSelectedCouponChange(coupon);
                              } else {
                                // setSelectedCoupon(null);
                                onSelectedCouponChange(null);
                              }
                            }}
                          />
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Form.Item>
            </div>
          </div>
        </>
      )}

      {preOrder && preOrder.userFullInfo && (
        <div className={styles.item}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.beforePointPrice !== cur.beforePointPrice}>
            {({ getFieldValue }) => {
              return (
                <>
                  <h3>
                    <PushpinTwoTone /> 积分抵现
                  </h3>
                  <div>
                    <Form.Item noStyle name={'pointNum'}>
                      <PointConsume
                        user={preOrder.userFullInfo.user}
                        pointRules={pointRules}
                        price={getFieldValue('beforePointPrice')}
                      />
                    </Form.Item>
                  </div>
                </>
              );
            }}
          </Form.Item>
        </div>
      )}

      <div className={styles.item}>
        <h3>
          <FallOutlined /> 特殊减免
        </h3>
        <div>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.beforeTotalPrice !== cur.beforeTotalPrice}>
            {({ getFieldValue }) => {
              return (
                <Form.Item noStyle name={'waiverAmount'}>
                  <WaiverAmountSelector beforeTotalPrice={getFieldValue('beforeTotalPrice')} />
                </Form.Item>
              );
            }}
          </Form.Item>
        </div>
      </div>

      <div className={styles.item}>
        <h3>
          <CarryOutTwoTone /> 交易清单如下，请收款
        </h3>
        <div>
          <Form.Item noStyle shouldUpdate={true}>
            {({ getFieldValue }) => {
              const writeOffCardUserList = getFieldValue(
                'writeOffCardUserList'
              ) as WriteOffCardUserList;
              return (
                <Descriptions column={2} size={'small'} layout="horizontal" bordered>
                  <Descriptions.Item label="商品总额">
                    <Price value={getFieldValue('goodsPrice')} />
                  </Descriptions.Item>
                  <Descriptions.Item label="商品数量">
                    {calcSpecificationsTotalNumber(preOrder.specifications)}
                  </Descriptions.Item>

                  {getFieldValue('couponPrice') && (
                    <Descriptions.Item span={2} label="优惠券抵扣">
                      <Price value={getFieldValue('couponPrice')} negative />
                    </Descriptions.Item>
                  )}

                  {getFieldValue('cardPrice') && (
                    <Descriptions.Item span={2} label="套餐卡抵扣">
                      <Price value={getFieldValue('cardPrice')} negative />
                    </Descriptions.Item>
                  )}
                  {writeOffCardUserList && (
                    <>
                      {writeOffCardUserList
                        .map(info => {
                          return (
                            <Descriptions.Item span={2} label="待核销" key={info.specificationId}>
                              {info.cardName}
                              <Typography.Text mark type="danger">
                                <CloseOutlined /> {info.number}
                              </Typography.Text>
                              {' - '}
                              {info.skuName}
                            </Descriptions.Item>
                          );
                        })
                        .filter(Boolean)}
                    </>
                  )}

                  {getFieldValue('pointPrice') && (
                    <Descriptions.Item span={2} label="积分抵扣">
                      <Price value={getFieldValue('pointPrice')} negative />
                    </Descriptions.Item>
                  )}

                  {getFieldValue('waiverAmount') && (
                    <Descriptions.Item span={2} label="特殊减免">
                      <Price value={getFieldValue('waiverAmount')} negative />
                    </Descriptions.Item>
                  )}

                  <Descriptions.Item label="合计应收额" span={2}>
                    <Price value={getFieldValue('totalPrice')} />
                  </Descriptions.Item>
                </Descriptions>
              );
            }}
          </Form.Item>
        </div>
      </div>
    </>
  );
};

export default CartTradeArea;
